import ArchiveOutlined from '@mui/icons-material/ArchiveOutlined';
import AssignmentReturnedOutlinedIcon from '@mui/icons-material/AssignmentReturnedOutlined';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckBoxOutlineBlankOutlinedIcon from '@mui/icons-material/CheckBoxOutlineBlankOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import DeliveryDiningOutlinedIcon from '@mui/icons-material/DeliveryDiningOutlined';
import DomainVerification from '@mui/icons-material/DomainVerification';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import HighlightOffOutlinedIcon from '@mui/icons-material/HighlightOffOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import LocationOffOutlinedIcon from '@mui/icons-material/LocationOffOutlined';
import ModeEditOutlineOutlinedIcon from '@mui/icons-material/ModeEditOutlineOutlined';
import PinDropOutlinedIcon from '@mui/icons-material/PinDropOutlined';
import PriorityHighOutlinedIcon from '@mui/icons-material/PriorityHighOutlined';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import RestoreOutlinedIcon from '@mui/icons-material/RestoreOutlined';
import WarehouseOutlinedIcon from '@mui/icons-material/WarehouseOutlined';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import useLabels from '@/hooks/useLabelsParcel';
import useResponsive from '@/hooks/useResponsive';
import { initializeHttpProvider } from '@/providers/HttpProvider/initializeHttpProvider';
import { RootState } from '@/redux/store';
import { getPDF } from '@/utils/common';

import type { Data } from '@/types/fulfill/statusOrders';
import type { IReqCancel, IResMapOK, IResOrderData } from '@/types/parcel/orders';
import type { OrdersData } from '@/types/parcel/orders/orders.interface';
import type { SvgIconTypeMap } from '@mui/material';

interface IMap {
  error: unknown;
  loading: boolean;
  mapUrl: string;
}

type OrderDetailsWithOrderData = {
  orderData: {
    trackingId?: string;
  };
};

type OrderDetailsWithId = {
  id: string;
};

type OrderDetailsWithTrackingId = {
  trackingId: string;
};

const hasOrderData = (details: unknown): details is OrderDetailsWithOrderData => {
  return Boolean(details && typeof details === 'object' && 'orderData' in details);
};

const hasId = (details: unknown): details is OrderDetailsWithId => {
  return Boolean(
    details &&
    typeof details === 'object' &&
    'id' in details &&
    typeof (details as Record<string, unknown>).id === 'string',
  );
};

const hasTrackingId = (details: unknown): details is OrderDetailsWithTrackingId => {
  return Boolean(
    details &&
    typeof details === 'object' &&
    'trackingId' in details &&
    typeof (details as Record<string, unknown>).trackingId === 'string',
  );
};

const COUNTRIES_WITHOUT_PRICES: string[] = [];
const CANCEL_INACTIVE_STATUSES = new Set([
  'Entregado',
  'Cancelado',
  'Devuelto',
  'fulfilled',
  'canceled',
  'returned',
]);

const iconMap: Record<string, OverridableComponent<SvgIconTypeMap>> = {
  '1001': ArchiveOutlined,
  '1002': DomainVerification,
  '2003': Inventory2OutlinedIcon,
  '2101': HighlightOffOutlinedIcon,
  '3004': DeliveryDiningOutlinedIcon,
  '4002': CheckCircleOutlineOutlinedIcon,
  '4003': CheckBoxOutlineBlankOutlinedIcon,
  '4101': CancelOutlinedIcon,
  '5002': AssignmentReturnedOutlinedIcon,
  '5101': BlockOutlinedIcon,
  '7401': PriorityHighOutlinedIcon,
  '7501': PriorityHighOutlinedIcon,
  '8001': HelpOutlineOutlinedIcon,
  '8002': HelpOutlineOutlinedIcon,
  '8003': CancelOutlinedIcon,
  '8004': HelpOutlineOutlinedIcon,
  '8005': LocationOffOutlinedIcon,
  '8006': HelpOutlineOutlinedIcon,
  '8101': CancelOutlinedIcon,
  '8102': CancelOutlinedIcon,
  '8201': PriorityHighOutlinedIcon,
  '9001': PinDropOutlinedIcon,
};

const useDetailsModal = (
  closeModal: () => void,
  trackingId?: string,
  isPickups?: boolean,
  onOrderCancelled?: () => void,
  orderDetails?: OrdersData | Data | IResOrderData | null,
) => {
  const isDesktop = useResponsive('up', 'lg');
  const [showActivities, setShowActivities] = useState(isPickups);
  const [map, setMap] = useState<IMap>({ error: null, loading: false, mapUrl: '' });
  const [cancelOrderModal, setCancelOrderModal] = useState(false);
  const [copyCode, setCopyCode] = useState<HTMLAnchorElement | HTMLSpanElement | null>(null);

  const { downloadLabels, loading: loadingLabel } = useLabels({});
  const { profile } = useSelector((state: RootState) => state.parcel);

  const popoverOpen = Boolean(copyCode);
  const idCopyCode = useMemo(() => (popoverOpen ? 'simple-popover' : undefined), [popoverOpen]);
  const country = profile.data?.country;

  const copy = async (event: React.MouseEvent<HTMLAnchorElement | HTMLSpanElement>, id: string) => {
    if (!isDesktop) return;
    setCopyCode(event.currentTarget);
    await navigator.clipboard.writeText(id);
    setTimeout(() => setCopyCode(null), 1000);
  };

  const fetchMap = useCallback(async (trackingId: string) => {
    setMap({ loading: true, error: null, mapUrl: '' });
    const http = initializeHttpProvider();
    try {
      const response = await http.orders.map({ trackingId });
      setMap({ loading: false, error: null, mapUrl: (response as IResMapOK)?.data || '' });
    } catch (error) {
      setMap({ loading: false, error, mapUrl: '' });
    }
  }, []);

  useEffect(() => {
    if (trackingId) fetchMap(trackingId);
  }, [trackingId, fetchMap]);

  const isCancelActive = (status?: string, statusCode?: string) => {
    if (!status) return false;

    if (isPickups && (country === 'CHL' || country === 'PER')) {
      const allowedPickupStatuses = ['1001', '1002', '2001', '2002'];
      return statusCode ? allowedPickupStatuses.includes(statusCode) : false;
    }

    return !CANCEL_INACTIVE_STATUSES.has(status);
  };

  const isOrderDelivered = (status?: string) => {
    if (!status) return false;

    const normalizedStatus = status.toLowerCase();

    const deliveredStates = ['entregado', 'entregada', 'delivered', 'fulfilled', '4002'];

    const isDelivered = deliveredStates.includes(normalizedStatus);
    return isDelivered;
  };

  const handleActivityIcon = (status: string) => {
    const code = parseInt(status);
    if (iconMap[status]) return iconMap[status];

    if (code >= 2000 && code < 3000) return LocalShippingIcon;
    if (code >= 3000 && code < 4000) return WarehouseOutlinedIcon;
    if (code >= 4000 && code < 5000) return LocalShippingIcon;
    if (code >= 5000 && code < 6000) return RestoreOutlinedIcon;
    if (code >= 6000 && code < 7000) return RateReviewOutlinedIcon;
    if (code >= 7000 && code < 8000) return ModeEditOutlineOutlinedIcon;
    if (code >= 8000 && code < 9000) return AssignmentTurnedInOutlinedIcon;
    return ArchiveOutlined;
  };

  const handleCancelOrder = async (data: IReqCancel) => {
    const http = initializeHttpProvider();
    try {
      await http.orders.cancel(data);
      toast.success('Orden cancelada con éxito');
      if (trackingId) fetchMap(trackingId);
      onOrderCancelled?.();
    } catch (error: unknown) {
      let errorMessage = 'Error al cancelar la orden';
      if (error && typeof error === 'object' && 'response' in error) {
        const response = (error as { response?: { data?: { message?: string } } }).response;
        errorMessage = response?.data?.message || errorMessage;
      }
      toast.error(errorMessage);
    } finally {
      setCancelOrderModal(false);
      closeModal();
    }
  };

  const handleOpenDownloadModal = async () => {
    let orderId: string | undefined;

    if (orderDetails) {
      if (hasOrderData(orderDetails)) {
        orderId = orderDetails.orderData?.trackingId;
      } else if (hasId(orderDetails)) {
        orderId = orderDetails.id;
      } else if (hasTrackingId(orderDetails)) {
        orderId = orderDetails.trackingId;
      }
    }

    if (orderId) {
      toast.promise(
        async () => {
          const http = initializeHttpProvider();
          const response = await http.statusOrder.downloadOrderLabel({ orderId });
          if (!response.data) throw new Error('Error al descargar la etiqueta');
          getPDF(response.data);
        },
        {
          pending: 'Descargando etiqueta...',
          success: 'Descarga exitosa',
          error: 'Ha ocurrido un error al intentar descargar la etiqueta',
        },
      );
    }
  };

  return {
    copy,
    copyCode,
    popoverOpen,
    idCopyCode,
    isCancelActive,
    showActivities,
    setShowActivities,
    map,
    countriesWithOutPrices: COUNTRIES_WITHOUT_PRICES,
    country,
    downloadLabels,
    loadingLabel,
    handleOpenDownloadModal,
    cancelOrderModal,
    handleOpenCancelOrderModal: () => setCancelOrderModal(true),
    handleCloseCancelOrderModal: () => setCancelOrderModal(false),
    handleActivityIcon,
    handleCancelOrder,
    isOrderDelivered,
  };
};

export default useDetailsModal;
